import {
  ArrowRight,
  Building2,
  Check,
  CheckCircle,
  Link2,
  Loader2,
  Search,
  XCircle,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { SplitScreenAuthLayout } from '@/components/layout/SplitScreenAuthLayout'
import { Alert } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { routePaths } from '@/lib/router'
import { authService } from '@/services/auth'
import {
  type OrganizationWithRole,
  organizationsService,
} from '@/services/organizations'
import { useAuthStore } from '@/stores/auth'

type ConnectionState =
  | 'idle'
  | 'unauthenticated'
  | 'invalid-link'
  | 'loading-orgs'
  | 'no-organizations'
  | 'select-org'
  | 'linking'
  | 'success'
  | 'already-connected'
  | 'error'

interface OrgOption {
  organization: OrganizationWithRole
  alreadyConnected: boolean
}

function validateBlueconicId(id: string | null): id is string {
  return typeof id === 'string' && id.length > 0 && id.length <= 100
}

function getOrgInitials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
}

export function ConnectBlueconicPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { isAuthenticated, switchOrganization } = useAuthStore()

  const blueconicAccountId = useRef(searchParams.get('blueconic_account_id'))
  const searchInputRef = useRef<HTMLInputElement>(null)

  const [state, setState] = useState<ConnectionState>('idle')
  const [errorMessage, setErrorMessage] = useState('')
  const [orgOptions, setOrgOptions] = useState<OrgOption[]>([])
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null)
  const [orgSearch, setOrgSearch] = useState('')

  const filteredOrgs = useMemo(() => {
    if (!orgSearch) return orgOptions
    const query = orgSearch.toLowerCase()
    return orgOptions.filter((opt) =>
      opt.organization.name.toLowerCase().includes(query)
    )
  }, [orgOptions, orgSearch])

  const connectableCount = useMemo(
    () => orgOptions.filter((o) => !o.alreadyConnected).length,
    [orgOptions]
  )

  const loadOrganizations = useCallback(async () => {
    setState('loading-orgs')
    try {
      const response = await organizationsService.getOrganizations()
      const orgs: OrgOption[] = response.organizations.map((org) => ({
        organization: org,
        alreadyConnected: !!org.hasCdpConnection,
      }))

      if (orgs.length === 0) {
        setState('no-organizations')
        return
      }

      setOrgOptions(orgs)

      const connectableOrgs = orgs.filter((o) => !o.alreadyConnected)

      if (connectableOrgs.length === 0) {
        setState('already-connected')
        return
      }

      if (orgs.length === 1) {
        await performConnection(orgs[0].organization.id)
      } else {
        setState('select-org')
      }
    } catch (err) {
      const error = err as { message?: string }
      setErrorMessage(error?.message || 'Failed to load organizations')
      setState('error')
    }
  }, [switchOrganization])

  useEffect(() => {
    if (!validateBlueconicId(blueconicAccountId.current)) {
      setState('invalid-link')
      return
    }

    if (!isAuthenticated()) {
      setState('unauthenticated')
      return
    }

    loadOrganizations()
  }, [isAuthenticated, loadOrganizations])

  useEffect(() => {
    if (
      state !== 'idle' &&
      state !== 'unauthenticated' &&
      state !== 'invalid-link' &&
      !isAuthenticated()
    ) {
      setState('unauthenticated')
    }
  }, [isAuthenticated, state])

  // Auto-focus search when org selector appears
  useEffect(() => {
    if (state === 'select-org') {
      // Small delay to let the DOM render
      const t = setTimeout(() => searchInputRef.current?.focus(), 50)
      return () => clearTimeout(t)
    }
  }, [state])

  async function performConnection(organizationId: string) {
    setState('linking')
    setErrorMessage('')
    try {
      await authService.connectBlueconic({
        blueconicAccountId: blueconicAccountId.current!,
        organizationId,
      })

      await switchOrganization(organizationId)

      setState('success')
    } catch (err) {
      const error = err as { message?: string }
      setErrorMessage(error?.message || 'Failed to connect BlueConic account')
      setState('error')
    }
  }

  function handleSignIn() {
    navigate(routePaths.login, {
      state: {
        from: {
          pathname: location.pathname,
          search: location.search,
        },
      },
    })
  }

  function handleSelectAndConnect() {
    if (selectedOrgId) {
      performConnection(selectedOrgId)
    }
  }

  function handleContinue() {
    navigate(routePaths.profileProperties)
  }

  function handleRetry() {
    setErrorMessage('')
    if (isAuthenticated()) {
      loadOrganizations()
    } else {
      setState('unauthenticated')
    }
  }

  const selectedOrg = orgOptions.find(
    (o) => o.organization.id === selectedOrgId
  )

  return (
    <SplitScreenAuthLayout>
      <div className="space-y-12">
        {/* Header */}
        <div className="space-y-3 text-left">
          <h1 className="font-medium text-[30px] leading-[36px] text-foreground">
            {state === 'success'
              ? "You're all set"
              : state === 'select-org'
                ? 'Choose an organization'
                : 'Connect BlueConic'}
          </h1>
          <p className="text-[14px] leading-[20px] text-muted-foreground">
            {state === 'success'
              ? 'Your BlueConic account has been linked.'
              : state === 'select-org'
                ? `${connectableCount} organization${connectableCount !== 1 ? 's' : ''} available to connect`
                : 'Link your BlueConic account to a Vwam organization'}
          </p>
        </div>

        {/* Invalid Link */}
        {state === 'invalid-link' && (
          <div className="space-y-4">
            <div className="rounded-2xl border border-red-200 bg-red-50/50 p-6 text-center space-y-3">
              <XCircle className="w-6 h-6 text-red-500 mx-auto" />
              <p className="text-[14px] text-red-700">
                This link is invalid or has expired. Please request a new
                connection link from BlueConic.
              </p>
            </div>
          </div>
        )}

        {/* Unauthenticated */}
        {state === 'unauthenticated' && (
          <div className="space-y-8">
            <div className="rounded-2xl border bg-muted/30 p-6 text-center space-y-3">
              <Link2 className="w-6 h-6 text-blue-600 mx-auto" />
              <p className="text-[14px] text-muted-foreground">
                Sign in to your Vwam account to complete the connection.
              </p>
            </div>
            <Button onClick={handleSignIn} className="w-full" size="lg">
              Sign in
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        )}

        {/* Loading */}
        {(state === 'idle' || state === 'loading-orgs') && (
          <div className="flex flex-col items-center py-12 space-y-3">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <p className="text-[13px] text-muted-foreground">
              Loading organizations...
            </p>
          </div>
        )}

        {/* No Organizations */}
        {state === 'no-organizations' && (
          <div className="rounded-2xl border border-orange-200 bg-orange-50/50 p-6 text-center space-y-3">
            <Building2 className="w-6 h-6 text-orange-500 mx-auto" />
            <p className="text-[14px] text-orange-700">
              You don't have access to any organizations. Please contact your
              administrator.
            </p>
          </div>
        )}

        {/* All Already Connected */}
        {state === 'already-connected' && (
          <div className="space-y-8">
            <div className="rounded-2xl border bg-muted/30 p-6 text-center space-y-3">
              <CheckCircle className="w-6 h-6 text-green-600 mx-auto" />
              <p className="text-[14px] text-muted-foreground">
                {orgOptions.length === 1
                  ? 'This organization is already connected to BlueConic.'
                  : 'All your organizations are already connected to BlueConic.'}
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate(routePaths.dashboard)}
              className="w-full"
              size="lg"
            >
              Go to Dashboard
            </Button>
          </div>
        )}

        {/* Org Selector */}
        {state === 'select-org' && (
          <div className="space-y-5">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                ref={searchInputRef}
                placeholder="Search organizations..."
                value={orgSearch}
                onChange={(e) => setOrgSearch(e.target.value)}
                className="h-9 rounded-full border-border bg-background pl-10 pr-3 py-1 text-[14px] shadow-sm focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:outline-none transition-[color,box-shadow]"
              />
            </div>

            {/* Org List */}
            <div className="max-h-[340px] overflow-y-auto -mx-1 px-1 space-y-1.5">
              {filteredOrgs.length === 0 ? (
                <p className="text-[13px] text-muted-foreground text-center py-8">
                  No organizations match your search
                </p>
              ) : (
                filteredOrgs.map((opt) => {
                  const isSelected = selectedOrgId === opt.organization.id
                  const isDisabled = opt.alreadyConnected

                  return (
                    <button
                      key={opt.organization.id}
                      type="button"
                      disabled={isDisabled}
                      onClick={() => setSelectedOrgId(opt.organization.id)}
                      className={`
                        w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-all
                        ${isDisabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}
                        ${isSelected && !isDisabled ? 'bg-primary/[0.06] ring-1 ring-primary/40' : ''}
                        ${!isSelected && !isDisabled ? 'hover:bg-muted/60' : ''}
                      `}
                    >
                      {/* Avatar */}
                      <div
                        className={`
                          flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center text-[12px] font-semibold
                          ${isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}
                          transition-colors
                        `}
                      >
                        {getOrgInitials(opt.organization.name)}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <p className="text-[14px] font-medium text-foreground truncate">
                          {opt.organization.name}
                        </p>
                        {opt.organization.role && (
                          <p className="text-[12px] text-muted-foreground">
                            {opt.organization.role.name}
                          </p>
                        )}
                      </div>

                      {/* Status */}
                      {isDisabled ? (
                        <Badge
                          variant="secondary"
                          className="flex-shrink-0 text-[11px] font-normal"
                        >
                          Connected
                        </Badge>
                      ) : isSelected ? (
                        <div className="flex-shrink-0 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                          <Check className="w-3 h-3 text-primary-foreground" />
                        </div>
                      ) : (
                        <div className="flex-shrink-0 w-5 h-5 rounded-full border-2 border-border" />
                      )}
                    </button>
                  )
                })
              )}
            </div>

            {/* Connect Button */}
            <div className="pt-2">
              <Button
                onClick={handleSelectAndConnect}
                disabled={!selectedOrgId}
                className="w-full"
                size="lg"
              >
                {selectedOrg ? (
                  <>
                    Connect {selectedOrg.organization.name}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                ) : (
                  'Select an organization'
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Linking */}
        {state === 'linking' && (
          <div className="flex flex-col items-center py-12 space-y-3">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <p className="text-[13px] text-muted-foreground">
              Connecting to BlueConic...
            </p>
          </div>
        )}

        {/* Success */}
        {state === 'success' && (
          <div className="space-y-8">
            <div className="rounded-2xl border bg-muted/30 p-6 text-center space-y-3">
              <CheckCircle className="w-6 h-6 text-green-600 mx-auto" />
              <p className="text-[14px] text-muted-foreground">
                Your BlueConic account is now linked. You can manage profile
                properties from your dashboard.
              </p>
            </div>
            <Button onClick={handleContinue} className="w-full" size="lg">
              Continue to Profile Properties
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        )}

        {/* Error */}
        {state === 'error' && (
          <div className="space-y-4">
            <Alert variant="destructive">{errorMessage}</Alert>
            <Button variant="outline" onClick={handleRetry} className="w-full">
              Try Again
            </Button>
          </div>
        )}
      </div>
    </SplitScreenAuthLayout>
  )
}
